import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AccountCircle from "@material-ui/icons/AccountCircle";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { styled } from "@mui/material/styles";
import Paper from "@material-ui/core/Paper";
import { ImageUpload } from "../../actions/image";
import { UserAction } from "../../actions/user";
import config from "../../config/config";

const RoundedDialogPaper = styled(Paper)({
  borderRadius: "16px !important",
  padding: "4px",
  width: "100%",
  maxWidth: "460px",
  boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
});

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
// "image/webp" is removed from ACCEPTED_TYPES because it is not supported by the backend yet. It can be added back once the backend supports it.

export default function UpdateProfilePictureModal({
  openModal,
  setOpenModal,
  currentProfilePic,
  onProfilePicUpdated,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  // Reset state when modal opens or closes
  useEffect(() => {
    if (!openModal) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setErrorMessage("");
      setUploading(false);
      setIsDragging(false);
      setPreviewOpen(false);
    }
  }, [openModal]);

  const handleClose = () => {
    if (uploading) return;
    setOpenModal(false);
  };

  const validateAndProcessFile = (file) => {
    setErrorMessage("");
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
      setErrorMessage(
        "Please select a valid image file (PNG, JPG, JPEG).",
        // "Please select a valid image file (PNG, JPG, JPEG, WEBP).",
      );
      return;
    }

    // 2. Extension check (new)
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      setErrorMessage("Please select a valid image file (PNG, JPG, JPEG).");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(
        "File size exceeds 5MB limit. Please choose a smaller image.",
      );
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndProcessFile(file);
    }
    // reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      validateAndProcessFile(files[0]);
    }
  };

  const handleRemoveSelected = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setErrorMessage("Please select an image first.");
      return;
    }

    setUploading(true);
    setErrorMessage("");

    try {
      // Step 1: Upload file to storage
      const formData = new FormData();
      formData.append("token", "staging");
      formData.append("files[0]", selectedFile);
      formData.append("files", selectedFile);

      const uploadRes = await ImageUpload.fetchImageUpload(formData);
      const uploadedImageUrl = uploadRes?.data?.fileUrls?.[0];

      if (!uploadedImageUrl) {
        throw new Error(
          uploadRes?.data?.message || "Failed to upload image to server.",
        );
      }

      // Step 2: Update user profile picture via config endpoint
      const userData = JSON.parse(localStorage.getItem("data") || "{}");
      const userId = userData?.id;

      const payload = {
        id: userId,
        userId: userId,
        image: uploadedImageUrl,
        profilePicture: uploadedImageUrl,
      };

      console.log(
        "Updating profile picture using config:",
        config.apiName.updateProfilePicture,
        payload,
      );
      const updateRes = await UserAction.updateProfilePicture(payload);

      if (updateRes !== null) {
        // Step 3: Update localStorage
        const updatedUserData = {
          ...userData,
          image: uploadedImageUrl,
          imgUrl: uploadedImageUrl,
          profilePicture: uploadedImageUrl,
        };
        localStorage.setItem("data", JSON.stringify(updatedUserData));

        if (onProfilePicUpdated) {
          onProfilePicUpdated(uploadedImageUrl);
        }

        alert("Profile picture updated successfully!");
        setOpenModal(false);
      } else {
        throw new Error("Failed to update profile picture in user profile.");
      }
    } catch (err) {
      console.error("Profile picture upload error:", err);
      setErrorMessage(
        err.message || "An unexpected error occurred. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const displayImage = previewUrl || currentProfilePic;

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      PaperComponent={RoundedDialogPaper}
    >
      {/* Modal Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#2563eb",
            }}
          >
            <CameraAltIcon style={{ fontSize: 18 }} />
          </div>
          <Typography
            variant="h6"
            style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}
          >
            Update Profile Picture
          </Typography>
        </div>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={uploading}
          style={{ color: "#64748b" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Modal Content */}
      <DialogContent style={{ padding: "20px 24px" }}>
        {/* Avatar preview area */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div
            onClick={() => displayImage && setPreviewOpen(true)}
            title={displayImage ? "Click to view photo" : ""}
            style={{
              position: "relative",
              width: 120,
              height: 120,
              borderRadius: "50%",
              border: "3px solid #3b82f6",
              padding: 3,
              background: "#fff",
              boxShadow: "0 8px 24px rgba(59, 130, 246, 0.18)",
              cursor: displayImage ? "pointer" : "default",
            }}
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt="Profile Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <AccountCircle
                style={{
                  width: "100%",
                  height: "100%",
                  color: "#cbd5e1",
                }}
              />
            )}

            {/* Camera badge trigger */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (!uploading) fileInputRef.current?.click();
              }}
              title="Select new image"
              style={{
                position: "absolute",
                bottom: 2,
                right: 2,
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                cursor: uploading ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                border: "2px solid #fff",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!uploading) e.currentTarget.style.transform = "scale(1.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <CameraAltIcon style={{ fontSize: 18 }} />
            </div>
          </div>
          <span
            style={{
              fontSize: 12,
              color: "#64748b",
              marginTop: 10,
              fontWeight: 500,
            }}
          >
            {previewUrl ? "Preview of new photo" : "Current profile picture"}
          </span>
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png, image/jpeg, image/jpg"
          // accept="image/png, image/jpeg, image/jpg, image/webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* Drag & Drop Upload Zone */}
        {!selectedFile ? (
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragging ? "#2563eb" : "#cbd5e1"}`,
              borderRadius: 12,
              padding: "24px 16px",
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              backgroundColor: isDragging ? "#eff6ff" : "#f8fafc",
              transition: "all 0.2s ease",
            }}
          >
            <CloudUploadIcon
              style={{
                fontSize: 40,
                color: isDragging ? "#2563eb" : "#94a3b8",
              }}
            />
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#1e293b",
                marginTop: 8,
              }}
            >
              Click to choose or drag & drop an image
            </Typography>
            <Typography
              style={{
                fontSize: 12,
                color: "#64748b",
                marginTop: 4,
              }}
            >
              Supports PNG, JPG, JPEG (Max 5MB)
            </Typography>
          </div>
        ) : (
          /* Selected File Info Card */
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                overflow: "hidden",
              }}
            >
              <img
                src={previewUrl}
                alt="Selected"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              <div style={{ overflow: "hidden" }}>
                <Typography
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#166534",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 220,
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography style={{ fontSize: 11, color: "#15803d" }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </div>
            </div>

            <div style={{ display: "flex", gap: 4 }}>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Change Image"
                style={{ color: "#15803d" }}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleRemoveSelected}
                disabled={uploading}
                title="Remove Selected"
                style={{ color: "#dc2626" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        )}

        {/* Error message */}
        {errorMessage && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {errorMessage}
          </div>
        )}
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions
        style={{
          padding: "14px 24px 20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={uploading}
          style={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: "#64748b",
            padding: "6px 16px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedFile || uploading}
          style={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: "#fff",
            backgroundColor: !selectedFile || uploading ? "#94a3b8" : "#2563eb",
            padding: "6px 20px",
            borderRadius: 8,
            boxShadow:
              !selectedFile || uploading
                ? "none"
                : "0 4px 14px rgba(37, 99, 235, 0.35)",
          }}
        >
          {uploading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CircularProgress size={16} style={{ color: "#fff" }} />
              <span>Saving...</span>
            </div>
          ) : (
            "Save Picture"
          )}
        </Button>
      </DialogActions>
      {/* Photo viewer – opens on top of the modal above */}
      <Dialog
        open={previewOpen && !!displayImage}
        onClose={() => setPreviewOpen(false)}
        style={{ zIndex: 1600 }} // stays above the first modal
        BackdropProps={{ style: { backgroundColor: "rgba(15, 23, 42, 0.75)" } }}
        PaperProps={{
          style: {
            width: "min(520px, 90vw)", // fixed width
            height: "min(520px, 90vh)", // fixed height
            margin: 16,
            borderRadius: 16,
            overflow: "hidden",
            background: "#0f172a",
            position: "relative",
            boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          },
        }}
      >
        <IconButton
          onClick={() => setPreviewOpen(false)}
          size="small"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            color: "#fff",
            background: "rgba(0,0,0,0.45)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={displayImage}
            alt="Profile"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // every photo fits the same box
            }}
          />
        </div>
      </Dialog>
    </Dialog>
  );
}
